import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import {
    type BrowseCategoryRawFacet,
    type BrowseCategoryRawFacetOption,
    getFacetOptionCandidates,
    getFacetTypeFromBrowseDimensionKey,
    resolveBrowseDimensionKeyForFacetId,
    resolveFacetOptionValuesFromSemanticSlugs,
    SEARCH_SEMANTIC_MULTI_VALUE_DELIMITER,
} from '@/utils/browsePath/browsePath';
import { getFlatFacetOwningCategoryId } from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

// A parsed URL holds strings (and numbers for page fields); the extra keys are the semantic dimension params.
export type CanonicalizableSearchParams = SearchUrlParams & Partial<Record<string, string | number>>;

type SemanticCategoryIdTarget = {
    dimensionKey: string;
    slug: string;
};

const splitParamValues = (value: string | number | undefined, delimiter: RegExp): string[] =>
    String(value ?? '')
        .split(delimiter)
        .map((segment) => segment.trim())
        .filter(Boolean);

const findOptionById = (
    options: BrowseCategoryRawFacetOption[],
    categoryId: string
): BrowseCategoryRawFacetOption | undefined => options.find((option) => String(option.id) === categoryId);

const getSemanticCategoryIdTarget = (
    categoryId: string,
    rawFacets: BrowseCategoryRawFacet[]
): SemanticCategoryIdTarget | undefined => {
    const flatFacet = getFlatFacetOwningCategoryId(rawFacets, categoryId);
    const originFacet = rawFacets.find((facet) => facet.id === FacetTypes.ORIGIN_ID);

    let dimensionKey: string | undefined;
    let option: BrowseCategoryRawFacetOption | undefined;
    if (flatFacet) {
        dimensionKey = resolveBrowseDimensionKeyForFacetId(flatFacet.id);
        option = findOptionById(flatFacet.options ?? [], categoryId);
    } else if (originFacet) {
        dimensionKey = resolveBrowseDimensionKeyForFacetId(FacetTypes.ORIGIN_ID);
        option = findOptionById(getFacetOptionCandidates(originFacet), categoryId);
    }

    const facetType = dimensionKey ? getFacetTypeFromBrowseDimensionKey(dimensionKey) : undefined;
    if (!dimensionKey || !facetType || !option?.name) {
        return undefined;
    }

    // The request resolves the slug back to an id against these same facets, and two options can share a
    // slug, so only move the id when the slug comes back as this exact id.
    const slug = urlSafeTitle(option.name);
    const [resolvedId] = resolveFacetOptionValuesFromSemanticSlugs(facetType, [slug], rawFacets);
    return resolvedId === categoryId ? { dimensionKey, slug } : undefined;
};

/**
 * Moves the `category_id` ids that belong to a browse dimension (creator, style, material, origin, subject) to that
 * dimension's semantic query param, so a URL built from a saved search reads like the search the user followed and
 * gets removable filter chips. Category ids and ids the facets can't map stay on `category_id`.
 * Returns `queryParams` itself when nothing moves.
 */
export const canonicalizeCategoryIdSearchParams = (
    queryParams: CanonicalizableSearchParams,
    rawFacets: BrowseCategoryRawFacet[]
): CanonicalizableSearchParams => {
    const categoryIds = splitParamValues(queryParams[paramTypes.CATEGORY_ID], /,/);
    if (!categoryIds.length || !rawFacets.length) {
        return queryParams;
    }

    const categoryTreeIds = new Set(
        getFacetOptionCandidates(rawFacets.find((facet) => facet.id === FacetTypes.CATEGORY_ID)).map((option) =>
            String(option.id)
        )
    );

    const remainingCategoryIds: string[] = [];
    const slugsByDimensionKey = new Map<string, string[]>();
    for (const categoryId of categoryIds) {
        const target = categoryTreeIds.has(categoryId) ? undefined : getSemanticCategoryIdTarget(categoryId, rawFacets);
        if (!target) {
            remainingCategoryIds.push(categoryId);
            continue;
        }
        slugsByDimensionKey.set(target.dimensionKey, [
            ...(slugsByDimensionKey.get(target.dimensionKey) ?? []),
            target.slug,
        ]);
    }

    if (!slugsByDimensionKey.size) {
        return queryParams;
    }

    const canonicalParams: CanonicalizableSearchParams = { ...queryParams };
    slugsByDimensionKey.forEach((slugs, dimensionKey) => {
        const existingSlugs = splitParamValues(canonicalParams[dimensionKey], /[~,]/);
        canonicalParams[dimensionKey] = [...new Set([...existingSlugs, ...slugs])].join(
            SEARCH_SEMANTIC_MULTI_VALUE_DELIMITER
        );
    });

    if (remainingCategoryIds.length) {
        canonicalParams[paramTypes.CATEGORY_ID] = remainingCategoryIds.join(',');
    } else {
        delete canonicalParams[paramTypes.CATEGORY_ID];
    }

    return canonicalParams;
};
